import React from 'react'

import { BaseInput, type InputProps } from '../Input'

/**
 * The `autocomplete` tokens a credential field may carry. Deliberately
 * narrow: `"off"`, `"one-time-code"` and free-form strings are rejected by
 * the type, because a field that wants any of those is not a credential and
 * belongs on the ignored `Input`.
 */
export type CredentialAutoComplete =
  'username' | 'email' | 'current-password' | 'new-password'

/**
 * The `autocomplete` tokens a *non*-credential field may carry — the
 * complement of {@link CredentialAutoComplete} for the ignored `Input`. A
 * credential token on an ignored field would compile to something extensions
 * skip while the browser still fills, so the two unions are kept disjoint:
 * a field wanting `"username"` or a password token belongs on
 * `CredentialInput`. Extend this list as non-credential needs arise; it is
 * deliberately not `string`.
 */
export type NonCredentialAutoComplete =
  | 'off'
  | 'on'
  | 'one-time-code'
  | 'name'
  | 'given-name'
  | 'family-name'
  | 'organization'
  | 'tel'
  | 'url'

export interface CredentialInputProps extends Omit<InputProps, 'autoComplete'> {
  /**
   * Required, not optional. #2405's root cause was an *omitted*
   * `autoComplete` falling back to browser heuristics on exactly the field
   * where it mattered; making the attribute unomittable on credential fields
   * encodes that lesson in the type system.
   */
  autoComplete: CredentialAutoComplete
}

/**
 * The deliberate opt-*in* to password managers: the same field as `Input`
 * but **without** the vendor ignore attributes, so an extension can fill
 * sign-in and offer to save or generate on sign-up and password reset.
 *
 * Use it for the credential fields of an auth form — username, email,
 * current password, new password — and nothing else. Identity fields
 * (first / last name), confirmation codes and every ordinary text input
 * stay on `Input`.
 *
 * @example
 * <CredentialInput label="Password" type="password" autoComplete="current-password" />
 */
const CredentialInput = React.forwardRef<
  HTMLInputElement,
  CredentialInputProps
>((props, ref) => <BaseInput {...props} ref={ref} />)

CredentialInput.displayName = 'CredentialInput'

export { CredentialInput }
export default CredentialInput
